import * as React from 'react';
import { cn } from 'utils/cn';
import { DayPicker, getDefaultClassNames, type DayButton, type DropdownProps, type Locale } from 'react-day-picker';
import { Select as SelectPrimitive } from '@base-ui/react/select';

import { Button, buttonVariants } from 'components/ui/button';
import { Select, SelectContent, SelectValue } from 'components/ui/select';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    buttonVariant = 'ghost',
    locale,
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn(
                'group/calendar bg-background p-3 [--cell-radius:9999px] [--cell-size:--spacing(9)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className
            )}
            captionLayout={captionLayout}
            locale={locale}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString(localeCode(locale), { month: 'short' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-fit', defaultClassNames.root),
                months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
                month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
                nav: cn('absolute inset-x-0 top-0 flex w-full items-center justify-end gap-1', defaultClassNames.nav),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
                    defaultClassNames.button_previous
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    'size-(--cell-size) p-0 select-none aria-disabled:opacity-50',
                    defaultClassNames.button_next
                ),
                month_caption: cn(
                    'flex h-(--cell-size) w-full items-center justify-start pr-[calc(var(--cell-size)*2)] pl-1',
                    defaultClassNames.month_caption
                ),
                dropdowns: cn(
                    'flex h-(--cell-size) w-full items-center justify-start gap-1.5 text-sm font-medium',
                    defaultClassNames.dropdowns
                ),
                caption_label: cn('text-sm font-medium select-none', defaultClassNames.caption_label),
                month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                    'text-muted-foreground flex-1 rounded-(--cell-radius) text-[0.8rem] font-normal select-none',
                    defaultClassNames.weekday
                ),
                week: cn('mt-2 flex w-full', defaultClassNames.week),
                week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
                week_number: cn('text-muted-foreground text-[0.8rem] select-none', defaultClassNames.week_number),
                day: cn(
                    'group/day relative aspect-square h-full w-full rounded-(--cell-radius) p-0 text-center select-none [&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
                    props.showWeekNumber
                        ? '[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
                        : '[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
                    defaultClassNames.day
                ),
                range_start: cn(
                    'bg-muted after:bg-muted relative isolate z-0 rounded-l-(--cell-radius) after:absolute after:inset-y-0 after:right-0 after:w-4',
                    defaultClassNames.range_start
                ),
                range_middle: cn('rounded-none', defaultClassNames.range_middle),
                range_end: cn(
                    'bg-muted after:bg-muted relative isolate z-0 rounded-r-(--cell-radius) after:absolute after:inset-y-0 after:left-0 after:w-4',
                    defaultClassNames.range_end
                ),
                today: cn(
                    'text-foreground [&:not([data-selected=true])>button]:ring-muted-foreground rounded-(--cell-radius) [&:not([data-selected=true])>button]:ring-1 [&:not([data-selected=true])>button]:ring-inset',
                    defaultClassNames.today
                ),
                outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaultClassNames.outside),
                disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
                hidden: cn('invisible', defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className, rootRef, ...props }) => {
                    return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
                },
                Chevron: ({ className, orientation, ...props }) => {
                    if (orientation === 'left') {
                        return <ChevronLeftIcon className={cn('size-4', className)} {...props} />;
                    }

                    if (orientation === 'right') {
                        return <ChevronRightIcon className={cn('size-4', className)} {...props} />;
                    }

                    return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
                },
                DayButton: ({ ...props }) => <CalendarDayButton locale={locale} {...props} />,
                Dropdown: CalendarDropdown,
                WeekNumber: ({ children, ...props }) => {
                    return (
                        <td {...props}>
                            <div className="flex size-(--cell-size) items-center justify-center text-center">
                                {children}
                            </div>
                        </td>
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
}

/**
 * The typecheck does not follow date-fns v4's `export type * from './locale/types.ts'`, so the `Locale` that
 * `DayPickerLocale` extends reads as empty and `locale.code` does not compile.
 */
function localeCode(locale: Partial<Locale> | undefined): string | undefined {
    return (locale as { code?: string } | undefined)?.code;
}

/**
 * An option of the calendar's dropdowns and of the time columns beside it: filled when selected, muted under the
 * pointer, ringed under keyboard focus.
 */
const pickerOptionClassName =
    'hover:bg-muted aria-selected:bg-primary aria-selected:text-primary-foreground focus-visible:ring-ring/50 h-8 rounded-md text-sm tabular-nums outline-none focus-visible:ring-3';

/**
 * The month or year dropdown as a kit Select, so its list is the app's popup rather than the browser's. DayPicker's
 * `onChange` reads only `event.target.value`.
 */
function CalendarDropdown({ options, value, onChange, disabled, className, 'aria-label': ariaLabel }: DropdownProps) {
    return (
        <Select<number>
            items={options}
            value={Number(value)}
            disabled={disabled}
            onValueChange={(next) =>
                onChange?.({ target: { value: String(next) } } as React.ChangeEvent<HTMLSelectElement>)
            }
        >
            <SelectPrimitive.Trigger
                aria-label={ariaLabel}
                className={cn(
                    'focus-visible:ring-ring/50 relative flex items-center gap-1 rounded-(--cell-radius) text-sm font-medium outline-none select-none focus-visible:ring-3 disabled:opacity-50',
                    className
                )}
            >
                <SelectValue />
                <ChevronDownIcon className="text-muted-foreground size-3.5" />
            </SelectPrimitive.Trigger>
            {/* Aligned on its trigger, the two-century year list would fill the viewport: it drops down at a fixed height. */}
            <SelectContent
                alignItemWithTrigger={false}
                align="start"
                className="max-h-[min(var(--available-height),18rem)] w-auto min-w-0 p-1"
            >
                {options?.map((option) => (
                    <SelectPrimitive.Item
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        className={cn(
                            pickerOptionClassName,
                            'flex cursor-default items-center px-2 whitespace-nowrap select-none data-disabled:pointer-events-none data-disabled:opacity-50'
                        )}
                    >
                        <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                    </SelectPrimitive.Item>
                ))}
            </SelectContent>
        </Select>
    );
}

function CalendarDayButton({
    className,
    day,
    modifiers,
    locale,
    ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
    const defaultClassNames = getDefaultClassNames();

    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString(localeCode(locale))}
            data-selected-single={
                modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                'group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-middle=true]:bg-muted data-[range-middle=true]:text-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground dark:hover:text-foreground relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 rounded-(--cell-radius) border-0 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-(--cell-radius) data-[range-end=true]:rounded-r-(--cell-radius) data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-(--cell-radius) data-[range-start=true]:rounded-l-(--cell-radius) [&>span]:text-xs [&>span]:opacity-70',
                defaultClassNames.day,
                className
            )}
            {...props}
        />
    );
}

export { Calendar, CalendarDayButton, pickerOptionClassName };
